import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { VlHttpService, VlDataService, HTTPAPIConfig, APIConfigMapping, PERMISSIONS_ACTIONS, APP_BUILDER_PERMISSIONS, AppPermissionsPipe } from "shared-library";
import { WidgetPropertyFormService } from "../../widgetPropertyForms/widget-property-form.service";
import { MatPaginator } from "@angular/material/paginator";
import { EntitySubmissionsService } from "../entity-submissions.service";
import { ALLOWED_INPUT_WIDGET_TYPES, WIDGET_TYPES } from "../../../constants/proj.cnst";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "lib-entity-entries-table",
  templateUrl: "./entity-entries-table.component.html",
  styleUrl: "./entity-entries-table.component.scss",
  providers: [AppPermissionsPipe]
})
export class EntityEntriesTableComponent implements OnInit,OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('deleteConfirm') deleteConfirm!: TemplateRef<any>;
  allowedwidgetTypes:string[] = ALLOWED_INPUT_WIDGET_TYPES;
  entries: any = [];
  displayedColumns: string[] = [];
  dataSource: any = [];
  // dataSource = new MatTableDataSource<any>(this.entries);
  widgets = [];
  displayedColumnsWithActions: string[] = [];
  editRowIndex: number | null = -1;
  editedEntry: any = null;
  totalItems = 0;
  pageSize = 10;
  currentPage = 0;
  pageNumber = 0;
  destroy$: Subject<void> = new Subject<void>();
  entityDetails: any = {};
  widgetTypes = WIDGET_TYPES;
  showNoData:boolean = false;
  showTable:boolean = false;
  fetchingEntries:boolean = false;
  selectedRows: { [submission_id: string]: boolean } = {}; // Store selected row submission_id
  appPermissions = APP_BUILDER_PERMISSIONS;
  permissionsActions = PERMISSIONS_ACTIONS;

  constructor(
    private toastrService: ToastrService,
    private httpService: VlHttpService,
    private dataService: VlDataService,
    private widgetPropertyFormService: WidgetPropertyFormService,
    private entitySubmissionsService: EntitySubmissionsService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private appPermissionsPipe: AppPermissionsPipe
  ) {}

  ngOnInit(): void {
    this.entitySubmissionsService.messageObservable.pipe(takeUntil(this.destroy$)).subscribe( async (entityDetails: any) => {
        if (Object.keys(entityDetails).length > 0) {
            this.resetTable();
            this.entityDetails = entityDetails;
            this.fetchingEntries = true;
            try{
              await this.fetchEntityWidget();
              await this.fetchEntityEntries();
            }catch(error){
              this.fetchingEntries = false;
              this.showTable = false;
              this.showNoData = true; // Show "No data" on error
            }
        }else{
            this.showTable = false;
            this.showNoData = false;
            this.fetchingEntries = false;
        }
    });
  }

  async fetchEntityWidget() {
    try{
      const listOfPageWidgetsOfEntity: any = await firstValueFrom(
        this.widgetPropertyFormService.fetchEntity(this.entityDetails?.entityId, this.entityDetails?.applicationsId)
      );
      
      this.widgets = this.widgetPropertyFormService.retriveWidgetsListFromEntiywithConfig(
        listOfPageWidgetsOfEntity?.data?.page_widget_details
      );
      this.widgets = this.widgets.filter((widget: any) => this.allowedwidgetTypes.includes(widget.widgetType));
      if (this.widgets.length > 0) {
        this.displayedColumns = this.widgets.map((widget: any) => widget.widgetLabel);
        const canEdit = this.appPermissionsPipe.transform([this.appPermissions.editData,this.permissionsActions.edit]);
        const canDelete = this.appPermissionsPipe.transform([this.appPermissions.deleteData,this.permissionsActions.edit]);
        this.displayedColumnsWithActions = [...this.displayedColumns];

        if (canEdit || canDelete) {
          if (canDelete) {
            this.displayedColumnsWithActions.unshift('select');
          }
          this.displayedColumnsWithActions.push('actions(E/D)');
        }
      } else {
        this.showNoData = true;
        this.showTable = false;
      }
    }catch(error:any){
      this.widgets = [];
      this.showTable = false;
      this.showNoData = true;
      this.fetchingEntries = false;
        this.toastrService.error("Failed to fetch data, please try again")
    }
  }
  fetchEntityEntries() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping["FETCH_ENTITY_ENTRIES"]);
    apiConfig.postData = {
      entity_id: this.entityDetails?.entityId ?? "",
      application_id: this.entityDetails?.applicationId ?? "",
      submission_id: null,
      relative_entities: true,
      user_id: null,
      pagination: true,
    };
    apiConfig.options = {
      params: {
        size: this.pageSize.toString() ?? "15",
        offset: this.pageNumber === 0 ? "1" : this.pageNumber.toString(),
      },
    };
    this.fetchingEntries = true; 
    this.showTable = false;
    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {
        if(data.submissions?.length > 0){
            this.showNoData = false;
            this.entries = data.submissions;
            this.dataSource = data.submissions;
            this.totalItems = data?.total_entries;
            this.currentPage = data?.current_page - 1;
            this.showTable=true
        }else{
            this.showNoData = true;
            this.showTable = false;
        }
        this.fetchingEntries = false;
        if(this.widgets.length < 1){
          this.showTable = false;
          this.showNoData = true;
        }
      },
      error: (err: any) => {
        this.showTable = false;
        this.fetchingEntries = false;
        this.toastrService.error("Failed to fetch data, please try again")
      },
    });
  }

  getFieldValue(entry: any, label: string): string {
    try {
      if (!entry || !entry.entity_values_json || !Array.isArray(entry.entity_values_json) || !label) {
        return "-";
      }
  
      const field = entry.entity_values_json.find(
        (f: any) => f && typeof f === "object" && f.widget_label === label
      );
  
      return field && field.value !== undefined ? field.value.toString() : "-";
    } catch (error) {
      console.error("Error in getFieldValue:", error);
      return "-";
    }
  }

  // Get field index dynamically
  getFieldIndex(entry: any, label: string): number {
    try {
      if (!entry || !Array.isArray(entry.entity_values_json) || !label) {
        return -1;
      }
  
      return entry.entity_values_json.findIndex(
        (f: any) => f && typeof f === "object" && f.widget_label === label
      );
    } catch (error) {
      console.error("Error in getFieldIndex:", error);
      return -1;
    }
  }
  

  // Edit row
  editRow(index: number, entry: any) {
    this.editRowIndex = index;
    this.editedEntry = structuredClone(entry); 
    this.editedEntry.entity_values_json = this.mergeWidgetData(this.widgets,this.editedEntry?.entity_values_json);
  }

  mergeWidgetData(widgets:any,entryValues:any) : any{
    let mappedWidgets:any = {};
    widgets.forEach((widget:any) => {
      if(widget?.widgetId){
        mappedWidgets[widget.widgetId] = mappedWidgets;
      }
      mappedWidgets[widget.widgetId] = widget;
    })
    let widgetIds = widgets.map((widget:any) => widget.widgetId);

    let mappedEntries:any = {};
      entryValues.forEach((singleField:any) => {
        if(singleField?.widget_id){
            mappedEntries[singleField.widget_id] = singleField;
        }
      })

    let newEntries:any = [];
    widgetIds.forEach((widgetId:string) => {
      if(mappedEntries[widgetId]){
        newEntries.push(mappedEntries[widgetId])
      }else if(mappedWidgets[widgetId]){
        newEntries.push({
            "value": "",
            "widget_id": mappedWidgets[widgetId].widgetId,
            "widget_type": mappedWidgets[widgetId].widgetType,
            "widget_label": mappedWidgets[widgetId].widgetLabel,
        })
      }
    })

    return newEntries;
  }

  saveEdit() {
    if (this.editRowIndex !== null) {
      this.editedEntry.entity_values_json = this.editedEntry?.entity_values_json.filter((singlefield: any) => singlefield.value.trim() !== "").map((singleField:any) => ({ ...singleField, value: typeof singleField.value === "string" ? singleField.value.trim() : singleField.value }))
      this.editEntityEntry(this.editedEntry);
      Object.assign(this.dataSource[this.editRowIndex], this.editedEntry); 
      this.dataSource = [...this.dataSource]
      this.cdr.detectChanges();
      this.editRowIndex = -1;
    }
  }

  // Cancel Editing
  cancelEdit() {
    this.editedEntry.entity_values_json = this.editedEntry?.entity_values_json.filter((singlefield: any) => singlefield.value.trim() !== "");
    this.editRowIndex = null;
    this.editedEntry = null;
  }
  // Delete row
  deleteRow(index: number, entry: any) {
    if (!entry) return;
    this.entries.splice(index, 1);
    this.dataSource = [...this.entries];
    let postData = {
      submission_id: [entry["submission_id"],],
    };
    this.deleteRowApi(postData);
    this.cdr.detectChanges();
  }

  editEntityEntry(entry: any) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping["EDIT_ENTITY_ENTRY"]);
    apiConfig.postData = {
      app_id: "3c84661d-40c3-44e0-bd69-2555ee9b8bba",
      data: [
        {
          submission_id: entry["submission_id"],
          data: entry["entity_values_json"],
        },
      ],
    };
    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        this.toastrService.success(data?.result ?? "operation success");
        this.editRowIndex = -1;
      },
      error: (err: any) => {
        this.toastrService.success("Fialed to edit data, please try agian")
      },
    });
  }

  deleteRowApi(postData: any) {
    this.fetchingEntries = true;
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping["DELETE_ENTITY_ENTRY"]);
    apiConfig.postData = postData
    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        this.editRowIndex = -1;
        this.toastrService.success("Data deleted successfully");
        this.fetchEntityEntries();
      },
      error: (err: any) => {
        this.toastrService.warning("Delete failed, please try again later");
      },
    });
  }

  onPageChange(event: any) {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.selectedRows = {};
    this.editRowIndex = -1;
    this.editedEntry = null;
    this.fetchingEntries = true; // Show spinner during page change
    this.showTable = false;
    this.fetchEntityEntries(); // Fetch new data from AP
  }

  ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
  }

  // Toggle all checkboxes
  toggleSelectAll(event: any) {
    const checked = event.checked;
    this.dataSource.forEach((row:any) => this.selectedRows[row.submission_id] = checked);
  }
  
  // Check if all rows are selected
  isAllSelected(): boolean {
    return this.dataSource.length > 0 && this.dataSource.every((row:any) => this.selectedRows[row.submission_id]);
  }
  
  // Check if some rows are selected (for indeterminate state)
  isPartialSelection(): boolean {
    return this.dataSource.some((row:any) => this.selectedRows[row.submission_id]) && !this.isAllSelected();
  }
  
  // Update individual row selection
  updateSelection(submission_id: string, checked: boolean) {
    if (!this.selectedRows.hasOwnProperty(submission_id)) {
      this.selectedRows[submission_id] = false; // Initialize if not present
    }
    this.selectedRows[submission_id] = checked;
  }
  
  // Check if any row is selected
  hasSelection(): boolean {
    return Object.values(this.selectedRows).some(selected => selected);
  }
  
  // Delete selected rows
  deleteSelected() {
    const selectedIds = Object.keys(this.selectedRows).filter(submission_id => this.selectedRows[submission_id]);
    let postData:any = {
      submission_id:selectedIds
    }
    this.deleteRowApi(postData);
    this.dataSource = this.dataSource.filter((row:any) => !selectedIds.includes(row.submission_id));
    this.selectedRows = {}; // Reset selection
    
  }

  openDeleteDialog(index: number | null) {
    const dialogRef = this.dialog.open(this.deleteConfirm, {
      width: '300px',
      data:index
    });

    dialogRef.afterOpened().pipe(takeUntil(this.destroy$)).subscribe(() => {
      dialogRef.keydownEvents().pipe(takeUntil(this.destroy$)).subscribe((event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          dialogRef.close(true);
        }
      });
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (confirmed) {
        if (index != null) {
          this.deleteRow(index,this.dataSource[index]);
        } else {
          this.deleteSelected();
        }
      }
    });
  }

  resetTable(){
    this.entries = [];
    this.displayedColumns = [];
    this.dataSource = [];
    this.widgets = [];
    this.displayedColumnsWithActions = [];
    this.editRowIndex = -1;
    this.editedEntry = null;
    this.totalItems = 0;
    this.pageSize = 10;
    this.currentPage = 0;
    this.pageNumber = 0;
    this.entityDetails = {};
    this.showNoData = false;
    this.showTable = false;
    this.fetchingEntries = false;
    this.selectedRows = {}; // Store selected row submission_id
  }

 }
